/* PAGE: HOME
   ========================================================================== */

import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  Avatar,
  Box,
  Button,
  Center,
  chakra,
  Container,
  Divider,
  Flex,
  Heading,
  Image,
  Link,
  SimpleGrid,
  Stack,
  Text,
  useColorMode,
  useColorModeValue,
  VisuallyHidden,
} from '@chakra-ui/react';
import { useMediaQuery } from '@material-ui/core';
import './index.scss';
import { message } from 'antd';
import { dispatch, useSelector } from 'common';
import { ApiConstants, NetWorkService } from 'library/networking';
import { WHITELIST_PERMISSION } from 'model/room.interface';
import { FaDiscord, FaGithub, FaTelegram, FaTwitter } from 'react-icons/fa';
import { appActions, homeActions } from 'redux/action-slice';
import ROUTES from 'routes/constant';

import Styled from './index.style';

// import { getUrlFromSVGText } from './svg/function';

const SocialButton = ({
  children,
  label,
  href,
}: {
  children: ReactNode;
  label: string;
  href: string;
}) => {
  return (
    <chakra.button
      // bg={useColorModeValue('blackAlpha.100', 'whiteAlpha.100')}
      rounded={'full'}
      w={8}
      h={8}
      cursor={'pointer'}
      as={'a'}
      href={href}
      display={'inline-flex'}
      alignItems={'center'}
      justifyContent={'center'}
      transition={'background 0.3s ease'}
      _hover={
        {
          // bg: useColorModeValue('blackAlpha.200', 'whiteAlpha.200'),
        }
      }>
      <VisuallyHidden>{label}</VisuallyHidden>
      {children}
    </chakra.button>
  );
};

const Home = () => {
  //state
  const [searchParams] = useSearchParams('code');
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const { pathname } = useLocation();
  const { userInfo } = useSelector(state => state.app);
  const [isWhitelist, setIsWhitelist] = useState(false);
  const bgButton = useColorModeValue(
    'linear-gradient(90deg, #7E4698 0%, #529DD0 100%)',
    'linear-gradient(90deg, #7E4698 0%, #529DD0 100%)',
  );
  const colorButton = 'white';
  const bg = useColorModeValue('bg-white', 'bg-antd');
  const textColor = useColorModeValue('text-black', 'text-white');

  const code = useMemo(() => searchParams.get('code'), [searchParams]);

  useEffect(() => {
    if (pathname.includes(ROUTES.linkTelegram) && code) {
      verifyTelegramOTP(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, code]);

  useEffect(() => {
    const checkPermission = async () => {
      if (userInfo?.wallet_address) {
        const permissionResponse = await NetWorkService.Get<WHITELIST_PERMISSION>({
          url: ApiConstants.CHECK_WHITELIST,
          params: {
            address: userInfo.wallet_address,
          },
        });

        if (permissionResponse?.data) {
          const { is_exist } = permissionResponse.data;
          setIsWhitelist(is_exist);
          dispatch(appActions.setIsWhitelist(is_exist));
        }
      } else {
        setIsWhitelist(false);
        dispatch(appActions.setIsWhitelist(false));
      }
    };

    checkPermission();
  }, [userInfo?.wallet_address]);

  const renderButton = useMemo(() => {
    const clickHandler = () => {
      if (isWhitelist) {
        launchApp();
      } else {
        handleNavigate();
      }
    };
    const labelText = isWhitelist ? 'Launch App' : 'Pre-Registration';
    return (
      <Button
        onClick={clickHandler}
        rounded={'full'}
        _hover={{ boxShadow: 'none', opacity: 0.75 }}
        bg={bgButton}
        color={colorButton}
        fontSize="lg"
        borderRadius="14px"
        px="30px"
        display={{
          sm: 'none',
          lg: 'flex',
        }}>
        {labelText}
      </Button>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWhitelist, bgButton]);

  const verifyTelegramOTP = async (otp: string) => {
    const response = await NetWorkService.Post<boolean>({
      url: ApiConstants.VERIFY_TELEGRAM_OTP,
      body: {
        otp,
      },
    });

    if (response?.data) {
      message.success('Link Telegram successfully');
      dispatch(
        homeActions.onGetUserInfo(data => {
          dispatch(appActions.onSetUserInfo(data));
        }),
      );
    } else {
      message.error((response?.message as string) || 'Unauthorized');
    }

    navigate(ROUTES.messages, { replace: true });
  };

  const handleNavigate = () => {
    const campaign = searchParams.get('campaign');
    navigate(campaign ? `${ROUTES.preRegistration}?campaign=${campaign}` : ROUTES.preRegistration);
  };

  const launchApp = () => {
    navigate(ROUTES.messages);
  };

  const isSmallScreen = useMediaQuery('(max-width: 768px)');

  //render
  return (
    <Styled.Container
      style={{ width: '100%', justifyContent: 'start' }}
      className={`${bg} relative`}>
      <Box
        backgroundImage="url('/images/grblue.jpg')"
        backgroundPosition="center"
        backgroundRepeat="no-repeat">
        <Container maxW={'2xl'}>
          <Stack
            as={Box}
            textAlign={'center'}
            spacing={{ base: 8, md: 14 }}
            py={{ base: 20, md: 36 }}>
            <Heading
              fontWeight={'bold'}
              fontSize={{ base: '4xl', md: '5xl', xl: '6xl' }}
              lineHeight={'110%'}>
              Web3 Communication <br />
              <Text as={'span'} color={'#7E4698'}>
                Protocol
              </Text>
            </Heading>
            <Text color={'gray.500'}>
              DMTP is a protocol that allows users to exchange
              <br /> messages based on their wallet addresses. <br />
              Users can send and rceive messages to each other, and Dapps can
              <br /> send notifications and messages to the users.
            </Text>
            <Stack
              direction={'column'}
              spacing={3}
              align={'center'}
              alignSelf={'center'}
              position={'relative'}>
              {renderButton}
              <Link
                color={'blue.400'}
                colorScheme={'blue'}
                isExternal
                rel="noopener,noreferrer"
                href="https://dmtp.gitbook.io/dmtp-docs">
                Document
              </Link>
            </Stack>
          </Stack>
        </Container>
      </Box>
      {/* </div> */}
      <div className={'section-common'}>
        <Flex justifyContent={'center'} alignItems={'center'} mb={10}>
          <Box w={'70%'}>
            <Stack
              as={Box}
              textAlign={'center'}
              spacing={{ base: 0, md: 0 }}
              py={{ base: 0, md: 0 }}
              p={'auto'}
              mb={50}>
              <Heading
                pt={20}
                fontWeight={600}
                fontSize={{ base: '5xl', md: '6xl', xl: '7xl' }}
                lineHeight={'110%'}
                className={`${textColor}`}>
                Features
              </Heading>
            </Stack>
            <Stack minH={'10vh'} direction={{ base: 'column', md: 'row' }} className="sm:">
              <Center flex={1}>
                {isSmallScreen ? (
                  <Image
                    mt={'auto'}
                    alt={'Login Image'}
                    src={'images/multichain.svg'}
                    width="334px"
                    height="240px"
                  />
                ) : (
                  <Center>
                    <Image
                      alt={'Login Image'}
                      src={'images/multichain.svg'}
                      width={{ base: '282px', md: '230px', lg: '334px' }}
                      height={{ base: '202px', md: '165px', lg: '240px' }}
                    />
                  </Center>
                )}
              </Center>
              <Center p={{ base: 2, md: 8 }} flex={1}>
                <Stack spacing={6} w={'full'} maxW={'lg'}>
                  {isSmallScreen ? (
                    <Heading
                      fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                      textAlign={{ base: 'center', md: 'left' }}>
                      <Text position={'relative'}>
                        Support <span color={'#7E4698'}>All Chains</span>
                      </Text>{' '}
                    </Heading>
                  ) : (
                    <Heading
                      fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                      textAlign={{ base: 'center', md: 'left' }}>
                      <Text
                        as={'span'}
                        position={'relative'}
                        className={`${colorMode === 'dark' ? 'text-white' : 'text-black'}`}>
                        Support
                      </Text>
                      <br />{' '}
                      <Text color={'#7E4698'} as={'span'}>
                        All Chains
                      </Text>{' '}
                    </Heading>
                  )}
                  <Text
                    fontSize={{ base: 'md', lg: 'lg' }}
                    color={'gray.500'}
                    textAlign={{ base: 'center', md: 'left' }}>
                    DMTP can support all chains such as EVM, ZK based chain, non-EVM L1, L2, L3....
                    You can view all messages and notifications sent to your wallet on each chain,
                    so DMTP is all you need as a communication tool in crypto.
                  </Text>
                  <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                    <Center>
                      <Link
                        href="https://dmtp.gitbook.io/dmtp-docs/dmtp-system/supported-chain"
                        isExternal>
                        <Button
                          rounded={'full'}
                          _hover={{ boxShadow: 'none', opacity: 0.75 }}
                          bg={bgButton}
                          color={colorButton}
                          fontSize="sm"
                          borderRadius="14px"
                          px="30px"
                          display={'flex'}>
                          Learn more
                        </Button>
                      </Link>
                    </Center>
                  </Stack>
                </Stack>
              </Center>
            </Stack>

            <Divider m={10} />

            <Stack minH={'10vh'} direction={{ base: 'column', md: 'row' }} p={'auto'}>
              <Center flex={1}>
                {isSmallScreen ? (
                  <Image
                    alt={'Login Image'}
                    objectFit={'cover'}
                    src={'images/incentives.svg'}
                    width="282px"
                    height="238px"
                  />
                ) : (
                  <Center pt={{ base: 0, xl: 100 }}>
                    <Image
                      alt={'Login Image'}
                      objectFit={'cover'}
                      src={'images/incentives.svg'}
                      width={{ base: '282px', md: '230px', lg: '334px' }}
                      height={{ base: '238px', md: '194px', lg: '282px' }}
                    />
                  </Center>
                )}
              </Center>
              <Center p={{ base: 2, md: 8 }} flex={1}>
                <Stack spacing={6} w={'full'} maxW={'lg'}>
                  <Heading
                    fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                    textAlign={{ base: 'center', md: 'left' }}>
                    <Text as={'span'} position={'relative'} className={`${textColor}`}>
                      Rewards For
                    </Text>
                    <br />{' '}
                    <Text color={'#7E4698'} as={'span'}>
                      User and Dapps
                    </Text>{' '}
                  </Heading>
                  <Text
                    fontSize={{ base: 'md', lg: 'lg' }}
                    color={'gray.500'}
                    textAlign={{ base: 'center', md: 'left' }}>
                    DMTP has an innovative and unique incentive model. Users can earn $DMTP tokens
                    based on the amount of activity at the DMTP. In addition, there are rewards for
                    actions that activate the DMTP ecosystem, such as inviting users via the SDK,
                    attracting Dapps, etc.
                  </Text>
                  <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                    <Center>
                      <Link
                        href="https://dmtp.gitbook.io/dmtp-docs/about-dmtp/rewards-for-users-and-dapps"
                        isExternal>
                        <Button
                          rounded={'full'}
                          _hover={{ boxShadow: 'none', opacity: 0.75 }}
                          bg={bgButton}
                          color={colorButton}
                          fontSize="sm"
                          borderRadius="14px"
                          px="30px"
                          display={'flex'}>
                          Learn more
                        </Button>
                      </Link>
                    </Center>
                  </Stack>
                </Stack>
              </Center>
            </Stack>

            <Divider m={10} />

            <Stack minH={'10vh'} direction={{ base: 'column', md: 'row' }} p={'auto'}>
              <Center flex={1}>
                {isSmallScreen ? (
                  <Image
                    alt={'Login Image'}
                    objectFit={'cover'}
                    src={'images/sticker.svg'}
                    width="240px"
                    height={'240px'}
                  />
                ) : (
                  <Center>
                    <Image
                      alt={'Login Image'}
                      objectFit={'cover'}
                      src={'images/sticker.svg'}
                      width={{ base: '282px', md: '230px', lg: '334px' }}
                      height={{ base: '282px', md: '230px', lg: '334px' }}
                    />
                  </Center>
                )}
              </Center>
              <Center p={{ base: 2, md: 8 }} flex={1}>
                <Stack spacing={6} w={'full'} maxW={'lg'}>
                  <Heading
                    fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                    textAlign={{ base: 'center', md: 'left' }}>
                    <Text
                      as={'span'}
                      position={'relative'}
                      textAlign={{ base: 'center', md: 'left' }}
                      className={`${textColor}`}>
                      Chat Sticker{' '}
                    </Text>
                    <Text
                      position={'relative'}
                      textAlign={{ base: 'center', md: 'left' }}
                      color={'#7E4698'}
                      as={'span'}>
                      NFT
                    </Text>
                  </Heading>
                  <Text
                    fontSize={{ base: 'md', lg: 'lg' }}
                    color={'gray.500'}
                    textAlign={{ base: 'center', md: 'left' }}>
                    DMTP allows you to send stickers as well as chats, some of which are available
                    only to NFT sticker holders and are created under copyright from the respective
                    NFT holders. A portion of NFT proceeds will be distributed to the copyright
                    provider. We are still looking for additional copyright donors.
                  </Text>
                  <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                    <Center>
                      <Link
                        href="https://dmtp.gitbook.io/dmtp-docs/about-dmtp/chat-sticker-nft"
                        isExternal>
                        <Button
                          rounded={'full'}
                          _hover={{ boxShadow: 'none', opacity: 0.75 }}
                          bg={bgButton}
                          color={colorButton}
                          fontSize="sm"
                          borderRadius="14px"
                          px="30px"
                          display={'flex'}>
                          Learn more
                        </Button>
                      </Link>
                    </Center>
                  </Stack>
                </Stack>
              </Center>
            </Stack>
          </Box>
        </Flex>
      </div>
      <div className="section-gradient section-common">
        <Stack as={Box} textAlign={'center'} spacing={{ base: 0, md: 0 }} p={15}>
          <Heading
            fontWeight={600}
            fontSize={{ base: '5xl', md: '6xl', xl: '7xl' }}
            lineHeight={'110%'}
            p={10}>
            Technical
          </Heading>
        </Stack>
        <Box p={6}>
          <SimpleGrid
            columns={{ base: 1, lg: 2, xl: 4 }}
            spacing={10}
            p={'auto'}
            justifyContent="center"
            alignItems={'center'}
            pb={20}>
            <Center>
              <Box
                maxW={{ base: '400px', sm: '500px', xl: '324px' }}
                w={'full'}
                // bg={useColorModeValue('white', 'gray.900')}
                boxShadow={'2xl'}
                rounded={'lg'}
                p={6}
                textAlign={'center'}
                className={`${bg}`}
                color={colorMode === 'dark' ? 'white' : 'black'}>
                <Avatar size={'xl'} src={'images/security.svg'} mb={4} pos={'relative'} />
                <Heading
                  fontSize={'2xl'}
                  fontFamily={'body'}
                  // color={useColorModeValue('gray.900', 'white')}
                >
                  End-to-End Encryption
                  <br />
                  <br />
                </Heading>
                <Text
                  textAlign={'center'}
                  // color={useColorModeValue('gray.700', 'gray.400')}
                  px={3}>
                  All P2P messages are E2EE (End-to-End Encryption), and no one, including DMTP, can
                  see the messages. Even if DMTP gets hacked or our key is compromised, your
                  messages are safe.
                </Text>
                <Stack
                  mt={8}
                  direction={'row'}
                  spacing={4}
                  textAlign={'center'}
                  justifyContent="center">
                  <Link
                    href={'https://dmtp.gitbook.io/dmtp-docs/dmtp-system/encryption-and-decryption'}
                    color={'blue.400'}
                    isExternal>
                    See more
                  </Link>{' '}
                </Stack>
              </Box>
            </Center>
            <Center>
              <Box
                maxW={{ base: '400px', sm: '500px', xl: '324px' }}
                w={'full'}
                // bg={useColorModeValue('white', 'gray.900')}
                boxShadow={'2xl'}
                rounded={'lg'}
                p={6}
                textAlign={'center'}
                className={`${bg}`}
                color={colorMode === 'dark' ? 'white' : 'black'}>
                <Avatar size={'xl'} src={'images/decentralized.svg'} mb={4} pos={'relative'} />
                <Heading
                  fontSize={'2xl'}
                  fontFamily={'body'}
                  // color={useColorModeValue('gray.900', 'white')}
                >
                  Decentralized & Permanence
                  <br />
                  <br />
                </Heading>
                <Text
                  textAlign={'center'}
                  // color={useColorModeValue('gray.700', 'gray.400')}
                  px={3}>
                  All messages exchanged via DMTP are managed in decentralized way. Even if DMTP
                  database is disappeared, you can access your past message data and decrypt it
                  using your key.
                </Text>

                <Stack
                  mt={8}
                  direction={'row'}
                  spacing={4}
                  textAlign={'center'}
                  justifyContent="center">
                  <Link
                    href={'https://dmtp.gitbook.io/dmtp-docs/dmtp-system/message-data-storage'}
                    color={'blue.400'}
                    isExternal>
                    See more
                  </Link>{' '}
                </Stack>
              </Box>
            </Center>
            <Center>
              <Box
                maxW={{ base: '400px', sm: '500px', xl: '324px' }}
                w={'full'}
                // bg={useColorModeValue('white', 'gray.900')}
                boxShadow={'2xl'}
                rounded={'lg'}
                p={6}
                textAlign={'center'}
                className={`${bg}`}
                color={colorMode === 'dark' ? 'white' : 'black'}>
                <Avatar size={'xl'} src={'images/customer.svg'} mb={4} pos={'relative'} />
                <Heading
                  fontSize={'2xl'}
                  fontFamily={'body'}
                  // color={useColorModeValue('gray.900', 'white')}
                >
                  <br />
                  Multi Clients
                  <br />
                  <br />
                </Heading>
                <Text
                  textAlign={'center'}
                  // color={useColorModeValue('gray.700', 'gray.400')}
                  px={3}>
                  DMTP supports all clients,and you can use it on your PC or mobile, and connect
                  DMTP to current SNS like Telegram or Discord, and enables you to get notified when
                  your wallet gets messages.
                </Text>

                <Stack
                  mt={8}
                  direction={'row'}
                  spacing={4}
                  textAlign={'center'}
                  justifyContent="center">
                  <Link
                    href={'https://dmtp.gitbook.io/dmtp-docs/dmtp-system/multi-client'}
                    color={'blue.400'}
                    isExternal>
                    See more
                  </Link>{' '}
                </Stack>
              </Box>
            </Center>
            <Center>
              <Box
                maxW={{ base: '400px', sm: '500px', xl: '324px' }}
                w={'full'}
                // bg={useColorModeValue('white', 'gray.900')}
                boxShadow={'2xl'}
                rounded={'lg'}
                p={6}
                textAlign={'center'}
                className={`${colorMode === 'dark' ? 'bg-antd' : 'bg-white'}`}
                color={colorMode === 'dark' ? 'white' : 'black'}>
                <Avatar size={'xl'} src={'images/sdk.svg'} mb={4} pos={'relative'} />
                <Heading
                  fontSize={'2xl'}
                  fontFamily={'body'}
                  // color={useColorModeValue('gray.900', 'white')}
                >
                  <br />
                  SDK
                  <br />
                  <br />
                </Heading>
                <Text
                  textAlign={'center'}
                  // color={useColorModeValue('gray.700', 'gray.400')}
                  px={3}>
                  DMTP provides a variety of SDKs that allow notifications to be sent directly to
                  users from Dapps smart contracts and front ends. Additionally, you can build a
                  chat function within Dapps.
                </Text>

                <Stack
                  mt={8}
                  direction={'row'}
                  spacing={4}
                  textAlign={'center'}
                  justifyContent="center">
                  <Link
                    href={'https://dmtp.gitbook.io/dmtp-docs/builders-guides/dmtp-sdk'}
                    color={'blue.400'}
                    isExternal>
                    See more
                  </Link>{' '}
                </Stack>
              </Box>
            </Center>
          </SimpleGrid>
        </Box>
      </div>
      <Flex
        justifyContent={'center'}
        alignItems={'center'}
        // bg={useColorModeValue('white', 'gray.900')}
      >
        <Box w={'90%'}>
          <Stack as={Box} textAlign={'center'} spacing={{ base: 0, md: 0 }} py={{ base: 0, md: 0 }}>
            <Heading
              pt={20}
              fontWeight={'bold'}
              fontSize={{ base: '3xl', sm: '4xl', md: '5xl' }}
              lineHeight={'110%'}
              className={`${textColor}`}>
              Backers
            </Heading>
          </Stack>
          <SimpleGrid
            columns={{ base: 1, md: 3 }}
            spacing={10}
            justifyContent="center"
            alignItems={'center'}>
            <Center>
              <Link href="https://east.vc/" isExternal>
                <Image alt={'Login Image'} objectFit={'cover'} src={'images/ev.svg'} />
              </Link>
            </Center>
            <Center>
              <Link href="https://arriba.studio/en/" isExternal>
                <Image alt={'Login Image'} objectFit={'cover'} src={'images/arriba.svg'} />
              </Link>
            </Center>
            <Center>
              <Link href="https://www.doublejump.tokyo/en" isExternal>
                <Image alt={'Login Image'} objectFit={'cover'} src={'images/djt.svg'} />
              </Link>
            </Center>
          </SimpleGrid>
          <Divider p={'auto'} />
          <Stack as={Box} textAlign={'center'} spacing={{ base: 0, md: 0 }} py={{ base: 0, md: 0 }}>
            <Heading
              pt={20}
              fontWeight={'bold'}
              fontSize={{ base: '3xl', sm: '4xl', md: '5xl' }}
              lineHeight={'110%'}
              className={`${textColor}`}>
              Supporters
            </Heading>
          </Stack>
          <Center>
            <SimpleGrid
              mt={10}
              columns={{ base: 3, sm: 4, md: 6, lg: 8 }}
              spacing={10}
              alignItems="center"
              justifyContent={'center'}>
              <Link href="https://twitter.com/alphabirds_xyz" isExternal>
                <Avatar size={'xl'} src={'images/alpha_birds.png'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/veefriends" isExternal>
                <Avatar size={'xl'} src={'images/vee_friends.png'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/KarafuruNFT" isExternal>
                <Avatar size={'xl'} src={'images/karafuru.jpg'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/SpaceRunnersNFT" isExternal>
                <Avatar size={'xl'} src={'images/space_runners.png'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/trait_sniper" isExternal>
                <Avatar size={'xl'} src={'images/trait_sniper.png'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/zenogakki" isExternal>
                <Avatar size={'xl'} src={'images/zenogakki.jpg'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/ChimpersNFT" isExternal>
                <Avatar size={'xl'} src={'images/chimpers.jpg'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/Coniunio" isExternal>
                <Avatar size={'xl'} src={'images/coniunio.png'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/coolmanuniverse" isExternal>
                <Avatar size={'xl'} src={'images/coolman.jpg'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/Alphazillanft" isExternal>
                <Avatar size={'xl'} src={'images/alphazilla.png'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/NWCcomm" isExternal>
                <Avatar
                  size={'xl'}
                  src={'images/nft_whale_community.png'}
                  mb={4}
                  pos={'relative'}
                />
              </Link>
              <Link href="https://twitter.com/RainbowAlpha_" isExternal>
                <Avatar size={'xl'} src={'images/rainbow_alpha.jpg'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/PlanetDegenNFT" isExternal>
                <Avatar size={'xl'} src={'images/planet_degen.jpg'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/SurgenceNFT" isExternal>
                <Avatar size={'xl'} src={'images/surgence.png'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/TBDKitchen" isExternal>
                <Avatar size={'xl'} src={'images/TBD_kitchen.jpg'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/techie_club" isExternal>
                <Avatar size={'xl'} src={'images/techie_club.png'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/voiders_world" isExternal>
                <Avatar size={'xl'} src={'images/voiders.png'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/WarriorsOfAnkh" isExternal>
                <Avatar size={'xl'} src={'images/warrior_of_ankh.jpg'} mb={4} pos={'relative'} />
              </Link>
              <Link href="" isExternal>
                <Avatar size={'xl'} src={'images/unknown2.png'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/Mintify" isExternal>
                <Avatar size={'xl'} src={'images/mintify.png'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/DarkEchelonn" isExternal>
                <Avatar size={'xl'} src={'images/dark_echelonn.png'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/illisted_" isExternal>
                <Avatar size={'xl'} src={'images/lllisted.jpg'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/FatCatsDao" isExternal>
                <Avatar size={'xl'} src={'images/fat_cats.png'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/DreamWorldNFT_" isExternal>
                <Avatar size={'xl'} src={'images/dream_world.png'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/Lapix_Zero" isExternal>
                <Avatar size={'xl'} src={'images/lapix_zero.png'} mb={4} pos={'relative'} />
              </Link>
              <Link href="https://twitter.com/limelight_kor" isExternal>
                <Avatar size={'xl'} src={'images/lime_light.jpg'} mb={4} pos={'relative'} />
              </Link>
            </SimpleGrid>
          </Center>
          <Divider />
          <Stack as={Box} textAlign={'center'} spacing={{ base: 0, md: 0 }} py={{ base: 0, md: 0 }}>
            <Heading
              pt={20}
              fontWeight={'bold'}
              fontSize={{ base: '3xl', sm: '4xl', md: '5xl' }}
              lineHeight={'110%'}
              className={`${textColor}`}>
              Partnerships
            </Heading>
          </Stack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mt={10}>
            <Center mt={10}>
              <Link href="https://www.mycryptoheroes.net/" isExternal>
                <Image
                  alt={'Login Image'}
                  objectFit={'cover'}
                  src={'images/mch.png'}
                  width={{ base: '300px', md: '200px', lg: '275px', xl: '375px' }}
                />
              </Link>
            </Center>
            <Center mt={10}>
              <Link href="https://www.kapital.gg/" isExternal>
                <Image
                  alt={'Login Image'}
                  objectFit={'cover'}
                  src={'images/kapital_dao.png'}
                  width={{ base: '312px', md: '200px', lg: '275px', xl: '375px' }}
                />
              </Link>
            </Center>
            <Center mt={10}>
              <Link href="https://www.mycryptoheroes.net/verse" isExternal>
                <Image
                  alt={'Login Image'}
                  objectFit={'cover'}
                  src={'images/mchverse.png'}
                  width={{ base: '300px', md: '200px', lg: '275px', xl: '375px' }}
                />
              </Link>
            </Center>
          </SimpleGrid>
        </Box>
      </Flex>
      <div className={'section-common'}>
        <Stack as={Box} textAlign={'center'} spacing={{ base: 0, md: 0 }} p={'auto'}>
          <Heading
            pt={20}
            fontWeight={'bold'}
            fontSize={{ base: '3xl', sm: '4xl', md: '5xl' }}
            lineHeight={'110%'}
            className={`${textColor}`}>
            Team
          </Heading>
        </Stack>
        <Box p={4}>
          <SimpleGrid columns={{ base: 1, lg: 3 }} p={'auto'}>
            <Center p={6}>
              <Box
                maxW={'400px'}
                w={'full'}
                // bg={useColorModeValue('white', 'gray.900')}
                boxShadow={'2xl'}
                rounded={'lg'}
                p={6}
                textAlign={'center'}
                className={`${bg}`}
                color={colorMode === 'dark' ? 'white' : 'black'}>
                <Avatar size={'xl'} src={'images/aru.jpeg'} mb={4} pos={'relative'} />
                <Heading
                  fontSize={'2xl'}
                  fontFamily={'body'}
                  // color={useColorModeValue('gray.900', 'white')}
                >
                  Aru Sasaki
                </Heading>
                <Text fontWeight={600} color={'gray.500'} mb={4}>
                  co-founder CEO
                </Text>
                {/* <Text>
                  Interned at a crypto VC while studying in New York. While conducting due
                  diligence, investment ratios, and research for portfolio projects, he also
                  supported managing a blog dedicated to blockchain. After returning to Japan, he
                  interned at Hokusai, an NFT infrastructure development company, as a member of the
                  board of directors and in public relations, and launched DMTP.
                </Text> */}

                <Stack mt={8} direction={'row'} spacing={4}>
                  <Link href="https://t.me/AruSasaki" isExternal flex={1}>
                    <Button
                      colorScheme="telegram"
                      leftIcon={<FaTelegram />}
                      size={{ base: 'md', lg: 'sm', xl: 'md' }}>
                      Telegram
                    </Button>
                  </Link>
                  <Link href="https://twitter.com/Aru_Web3en" isExternal flex={1}>
                    <Button
                      colorScheme="twitter"
                      leftIcon={<FaTwitter />}
                      size={{ base: 'md', lg: 'sm', xl: 'md' }}>
                      Twitter
                    </Button>
                  </Link>
                </Stack>
              </Box>
            </Center>
            <Center p={6}>
              <Box
                maxW={'400px'}
                w={'full'}
                // bg={useColorModeValue('white', 'gray.900')}
                boxShadow={'2xl'}
                rounded={'lg'}
                p={6}
                textAlign={'center'}
                className={`${bg}`}
                color={colorMode === 'dark' ? 'white' : 'black'}>
                <Avatar size={'xl'} src={'images/kosuke.jpeg'} mb={4} pos={'relative'} />
                <Heading
                  fontSize={'2xl'}
                  fontFamily={'body'}
                  // color={useColorModeValue('gray.900', 'white')}
                >
                  Kosuke Araki
                </Heading>
                <Text fontWeight={600} color={'gray.500'} mb={4}>
                  co-founder COO
                </Text>
                {/* <Text>
                  Continuous entrepreneur. Started many companies, including BigData, Adtech, and
                  D2C. He started "SecondLife" of Metaverse consulting for major companies at
                  Meltingdots. In 2015 he co-founded a project with CZ (Binance) to launch a
                  cryptocurrency exchange in Japan. In 2022, he found DMTP with Aru.
                </Text> */}

                <Stack mt={8} direction={'row'} spacing={4} p={'auto'}>
                  <Center flex={1}>
                    <Link href="https://t.me/kosuke_araki" isExternal>
                      <Button
                        colorScheme="telegram"
                        leftIcon={<FaTelegram />}
                        size={{ base: 'md', lg: 'sm', xl: 'md' }}>
                        Telegram
                      </Button>
                    </Link>
                  </Center>
                  <Center flex={1}>
                    <Link href="https://twitter.com/kosuke_araki" isExternal>
                      <Button
                        colorScheme="twitter"
                        leftIcon={<FaTwitter />}
                        size={{ base: 'md', lg: 'sm', xl: 'md' }}>
                        Twitter
                      </Button>
                    </Link>
                  </Center>
                </Stack>
              </Box>
            </Center>
            <Center p={6}>
              <Box
                maxW={'400px'}
                w={'full'}
                // bg={useColorModeValue('white', 'gray.900')}
                boxShadow={'2xl'}
                rounded={'lg'}
                p={6}
                textAlign={'center'}
                className={`${bg}`}
                color={colorMode === 'dark' ? 'white' : 'black'}>
                <Avatar size={'xl'} src={'images/yuzu.png'} mb={4} pos={'relative'} />
                <Heading
                  fontSize={'2xl'}
                  fontFamily={'body'}
                  // color={useColorModeValue('gray.900', 'white')}
                >
                  0xYuzu
                </Heading>
                <Text fontWeight={600} color={'gray.500'} mb={4}>
                  CTO
                </Text>
                {/* <Text>
                  Worked at a blockchain consulting firm in Japan. Implementing blockchain
                  technology for major companies and developing NFT issuance tools and NFT
                  cross-chain bridges. Personally participated in the ETH Global hackathon and won
                  multiple prizes. In November of 2022, he became the CTO of DMTP.
                </Text> */}

                <Stack mt={8} direction={'row'} spacing={4}>
                  <Center flex={1}>
                    <Link href="https://t.me/yuzurypto" isExternal>
                      <Button
                        colorScheme="telegram"
                        leftIcon={<FaTelegram />}
                        size={{ base: 'md', lg: 'sm', xl: 'md' }}>
                        Telegram
                      </Button>
                    </Link>
                  </Center>
                  <Center flex={1}>
                    <Link href="https://twitter.com/0x_Yuzu" isExternal>
                      <Button
                        colorScheme="twitter"
                        leftIcon={<FaTwitter />}
                        size={{ base: 'md', lg: 'sm', xl: 'md' }}>
                        Twitter
                      </Button>
                    </Link>
                  </Center>
                </Stack>
              </Box>
            </Center>
          </SimpleGrid>
        </Box>
      </div>

      <div className="section-gradient section-common">
        <Box color={useColorModeValue('gray.700', 'gray.200')}>
          <Container as={Stack} maxW={'6xl'} py={10}>
            <SimpleGrid templateColumns={{ sm: '1fr 1fr', md: '2fr 1fr 1fr 2fr' }} spacing={8}>
              <Stack direction={'row'} spacing={6}>
                <SocialButton label={'Twitter'} href={'https://twitter.com/DMTPHQ'}>
                  <FaTwitter />
                </SocialButton>
                <SocialButton label={'Discord'} href={'https://discord.com/invite/HyQ25C5n7s'}>
                  <FaDiscord />
                </SocialButton>
                <SocialButton label={'Github'} href={'https://github.com/DMTProtocol'}>
                  <FaGithub />
                </SocialButton>
              </Stack>
            </SimpleGrid>
          </Container>
        </Box>
      </div>
    </Styled.Container>
  );
};

export default Home;
